import { useMakeModelContext } from '../../../../../components/SearchPage/features/makeModel/MakeModelContext';
import { useMakeModelShared } from '../../../../../components/SearchPage/features/makeModel/hooks/MakeModelShared.hook';

import { asString } from '../../../../../utils/query-parameters';
import { mapMakeModelQuery } from '../../../../../components/SearchPage/features/makeModel/MakeModel.map';
import { mapGetAdsRequestPayload } from '../../../../../features/filters/Filters.mapper';
import { useRouter } from 'next/router';
import { useFiltersState } from '../../../../../features/filters/Filters.context';
import { shouldShowTrims } from '../MakeModel.constants';
import { mapTrimValues } from '../../../helpers/formatting';

type UseMakeModelReadURlArgs = {
  currentMake?: string;
  currentModel?: string;
  currentMultipleMakeAndOrModels?: string | Array<string>;
  currentSection?: string;
  makeModelEditable?: string | Array<string>;
  currentTrim?: string | Array<string>;
};

const useMakeModelReadURL = (args: UseMakeModelReadURlArgs) => {
  const {
    currentMake,
    currentModel,
    currentMultipleMakeAndOrModels,
    currentSection = 'cars',
    makeModelEditable,
    currentTrim,
  } = args;

  const { query } = useRouter();
  const { filtersData } = useFiltersState();

  const showTrims = shouldShowTrims(currentSection);

  const { setMakeModels } = useMakeModelContext();
  const { getModelsWithCounts, getTrimsWithCount } = useMakeModelShared({
    section: currentSection,
  });

  const mapMakeModels = () => {
    const makeModelEditableValueQueryValue = asString(makeModelEditable);
    // query is formatted as makeModelEditable = make_model_<index>
    const [, , editableMakeModelIndex] = makeModelEditableValueQueryValue
      ? makeModelEditableValueQueryValue.split('_')
      : '';

    if (currentMake) {
      const isEditable = parseInt(editableMakeModelIndex) === 0;

      if (isEditable) {
        const payload = mapGetAdsRequestPayload({ query, filtersData });

        getModelsWithCounts(currentMake, payload);

        if (currentModel && showTrims) {
          getTrimsWithCount(currentMake, currentModel, payload);
        }
      }

      setMakeModels([
        {
          make: currentMake,
          model: currentModel ? [currentModel] : [],
          isEditable,
          trim: mapTrimValues(currentTrim),
        },
      ]);
    } else if (Array.isArray(currentMultipleMakeAndOrModels)) {
      const mappedMakesAndModels = currentMultipleMakeAndOrModels.map(
        (make, index) => {
          return handleMultipleMakesAndModels(
            make,
            parseInt(editableMakeModelIndex) === index,
          );
        },
      );

      setMakeModels(mappedMakesAndModels);
    } else if (typeof currentMultipleMakeAndOrModels === 'string') {
      const mappedMakeAndModels = [
        handleMultipleMakesAndModels(
          currentMultipleMakeAndOrModels,
          parseInt(editableMakeModelIndex) === 0,
        ),
      ];
      setMakeModels(mappedMakeAndModels);
    } else {
      setMakeModels();
    }
  };

  const handleMultipleMakesAndModels = (
    makeModelValue: string,
    isEditable: boolean,
  ) => {
    const mappedMakeAndModels = mapMakeModelQuery(makeModelValue, isEditable);

    const { make: currentMake, model: currentModel } = mappedMakeAndModels;

    if (isEditable) {
      const payload = mapGetAdsRequestPayload({ query, filtersData });

      getModelsWithCounts(currentMake, payload);

      if (currentModel && showTrims) {
        getTrimsWithCount(currentMake, currentModel[0], payload);
      }
    }

    return mappedMakeAndModels;
  };

  return {
    mapMakeModels,
  };
};

export { useMakeModelReadURL };
